import { Timestamp } from 'firebase/firestore';

import dayjs, { Dayjs } from 'dayjs';

export function generateFirestoreId(): string {
  const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  let autoId = '';

  for (let i = 0; i < 20; i++) {
    autoId += CHARS.charAt(Math.floor(Math.random() * CHARS.length));
  }
  return autoId;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export function convertStringToType<T extends Record<string, any>>(value: string, key: string, obj: T): any {
  // Handle array paths like 'days[0].datetime'
  if (key.includes('[') && key.includes(']')) {
    const [arrayName, indexStr, fieldName] = key.match(/(\w+)\[(\d+)\]\.(\w+)/) ?? [];
    if (arrayName && indexStr && fieldName) {
      const index = parseInt(indexStr);
      // Create array if it doesn't exist
      (obj as any)[arrayName] ??= [];
      // Create object at index if it doesn't exist
      (obj as any)[arrayName][index] ??= {};
      // Convert the value based on the field type
      return convertBasicStringToType(value, fieldName);
    }
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */

  // Handle basic types
  return convertBasicStringToType(value, key);
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function convertBasicStringToType(value: string, key: string): any {
  /* eslint-enable @typescript-eslint/no-explicit-any */
  // TODO: This is storing as local time. We are going to want to be smarter about timezones.
  if (key.includes('datetime') || key.includes('At') || key.includes('Datetime')) {
    return Timestamp.fromDate(new Date(value));
  }
  if (key.startsWith('is')) {
    return value.toLowerCase() === 'true';
  }
  if (typeof value === 'string' && /^\d+$/.test(value)) {
    return parseInt(value);
  }
  return value;
}

// determine if a timestamp.now() is between 11:00pm and 12:00am
// if so, return true, else return false
export function IsEndOfDay(timestamp: Timestamp, timezone?: string): boolean {
  const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const date = new Date(timestamp.seconds * 1000);
  const localHour = date.toLocaleString('en-US', {
    hour: 'numeric',
    hour12: false,
    timeZone: tz,
  });

  return parseInt(localHour) === 23;
}

export function IsStartOfDay(timestamp: Timestamp, timezone?: string): boolean {
  const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const date = new Date(timestamp.seconds * 1000);
  const localHour = date.toLocaleString('en-US', {
    hour: 'numeric',
    hour12: false,
    timeZone: tz,
  });

  return parseInt(localHour) === 0;
}

export function IsWithin24Hours(timestamp: Timestamp): boolean {
  const now = new Date();
  const target = new Date(timestamp.seconds * 1000);

  const diffInHours = (target.getTime() - now.getTime()) / (1000 * 60 * 60);
  return diffInHours > 0 && diffInHours <= 24;
}

export function GetFirstAndLastName(fullName: string): [firstName: string, lastName: string] {
  const regex = /^(\p{L}+(?:\s+\p{L}+)*)\s+(\p{L}+(?:-\p{L}+)*)$/u;

  if (fullName) {
    const matches = fullName.trim().match(regex);
    if (matches) {
      return [matches[1], matches[2]];
    } else {
      return [fullName, ''];
    }
  } else {
    return ['', ''];
  }
}

export function GetFirstName(fullName: string): string {
  return GetFirstAndLastName(fullName)[0];
}

export function FormatPhoneNumber(phoneNumberString: string) {
  const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    const intlCode = match[1] ? '+1 ' : '';
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }

  return '';
}

export function DisplayDate(date: Date, tz: string, format?: string): string {
  dayjs.tz.setDefault(tz ? tz : Intl.DateTimeFormat().resolvedOptions().timeZone);
  return dayjs.tz(date, tz).format(format ? format : 'MMM D, YYYY');
}

export function DisplayDates(dates: Date[], tz: string, format?: string, isTwoLine?: boolean): string {
  const sortedDates = dates.sort((a, b) => a.valueOf() - b.valueOf());
  const startDate = sortedDates[0];
  const endDate = sortedDates[sortedDates.length - 1];

  if (dates.length === 0) {
    return '';
  } else if (dates.length === 1) {
    return DisplayDate(startDate, tz, format);
  } else {
    if (startDate.getFullYear() === endDate.getFullYear()) {
      if (startDate.getMonth() === endDate.getMonth()) {
        return (
          startDate.toLocaleString('default', { month: 'short' }) +
          ' ' +
          startDate.getDate().toString() +
          '-' +
          endDate.getDate().toString() +
          ', ' +
          endDate.getFullYear().toString()
        );
      } else {
        if (isTwoLine) {
          return (
            startDate.toLocaleString('default', { month: 'short' }) +
            ' ' +
            endDate.toLocaleString('default', { month: 'short' }) +
            '\n' +
            startDate.getDate().toString() +
            ' ' +
            endDate.getDate().toString() +
            ', ' +
            endDate.getFullYear().toString()
          );
        } else {
          return (
            startDate.toLocaleString('default', { month: 'short' }) +
            ' ' +
            startDate.getDate().toString() +
            '-' +
            endDate.toLocaleString('default', { month: 'short' }) +
            ' ' +
            endDate.getDate().toString() +
            ', ' +
            endDate.getFullYear().toString()
          );
        }
      }
    } else {
      // TODO: Handle multi-year events.
      return (
        startDate.toLocaleString('default', { month: 'short' }) +
        ' ' +
        startDate.getDate().toString() +
        ', ' +
        startDate.getFullYear().toString() +
        ' - ' +
        endDate.toLocaleString('default', { month: 'short' }) +
        ' ' +
        endDate.getDate().toString() +
        ', ' +
        endDate.getFullYear().toString()
      );
    }
    // timezone is number of hours relative to UTC. SO PST would be -8
    //return new Date(/*(*/datetime.seconds /*+ (timezone * 60 * 60))*/ * 1000)
    //  .toLocaleDateString('en-US');
  }
}

export function CombineDateJSTimeJS(date: Dayjs, time: Dayjs): Timestamp {
  const combined = date.set('hour', time.hour()).set('minute', time.minute());
  return Timestamp.fromDate(combined.toDate());
}

export function DisplayTime(datetime: Timestamp, tz?: string, format?: string): string {
  // console.log('DisplayTime', datetime, tz, format);
  const timezone = tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  dayjs.tz.setDefault(timezone);

  return dayjs.tz(datetime.toDate()).format(format ?? 'h:mm A');
}

export function DisplayDateTime(datetime: Timestamp, tz: string): string {
  return DisplayDate(datetime.toDate(), tz) + ' ' + DisplayTime(datetime, tz);
}

export function CurrentDateTime(): Timestamp {
  return Timestamp.now();
}

export function CurrentDate(): Timestamp {
  return Timestamp.fromDate(new Date());
}

export function FromDayJS(dayjs: Dayjs | null): Timestamp {
  return dayjs != null ? Timestamp.fromDate(dayjs.utc().toDate()) : Timestamp.now();
}

export function ToDayJS(datetime: Timestamp, timezone?: string): Dayjs {
  if (datetime === null || datetime.seconds === null) {
    datetime = Timestamp.now();
  }

  if (timezone === null) {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  return dayjs.tz(datetime.toDate(), timezone);
}

export function HoursMinutesToMilliseconds(hours: number, minutes: number): number {
  return hours * 60 * 60 * 1000 + minutes * 60 * 1000;
}

export function TimezoneList(): { value: number; label: string }[] {
  return [
    { value: -8, label: 'PST' },
    { value: -7, label: 'PDT' },
    { value: -6, label: 'MDT' },
  ];
}

export const FormatShortDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const ReformatGhinDate = (date: string, prefix?: string, suffix?: string): string => {
  const year = date.substring(0, 4);
  const month = date.substring(4, 6);
  const day = date.substring(6, 8);

  const dateObj = new Date(`${year}-${month}-${day}`);
  const formattedDate = dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return `${prefix}${formattedDate}${suffix}`;
};

// Use a fixed non-leap year for month/day conversion
const FIXED_YEAR = 2023;

/**
 * Convert MonthDay to Dayjs
 */
export function monthDayToDayjs(monthDay: { month: number; day: number }): Dayjs {
  return dayjs(`${FIXED_YEAR}-${monthDay.month}-${monthDay.day}`);
}

/**
 * Convert Dayjs to MonthDay
 */
export function dayjsToMonthDay(date: Dayjs | null): { month: number; day: number } | null {
  if (!date || !date.isValid()) return null;
  return { month: date.month() + 1, day: date.date() };
}

/**
 * Check if a year is a leap year
 */
export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

/**
 * Create a Timestamp from a MonthDay and year.
 * Handles leap year edge case for Feb 29 - uses Feb 28 if not a leap year.
 */
export function createTimestampFromMonthDay(monthDay: { month: number; day: number }, year: number): Timestamp {
  const { month } = monthDay;
  let { day } = monthDay;

  // Handle Feb 29 in non-leap years
  if (month === 2 && day === 29 && !isLeapYear(year)) {
    day = 28;
  }

  // Month is 0-based in JavaScript Date, so subtract 1
  return Timestamp.fromDate(new Date(year, month - 1, day));
}
